import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createCustomData } from '../Sos2/hoverContent';   // the EXACT app hover (white, metric-aware)
import { softFill } from '../Sos2/Constants';   // one score ramp, shared with the LR timeline

// The strip. One horizontal row per requested grain, cells in screenplay order,
// coloured by whatever metric prepareChartData computed (node.color). Extras: a left
// gutter of grain labels; a "Script" row (the DER 'movie' node, labelled with the film's
// title); width-guarded cell
// labels; act/sequence boundary rules that drop DOWN from their own row; and the exact
// app tooltip (reused via createCustomData) portalled to <body> so it isn't trapped by
// a transformed ancestor. The tooltip content follows the selected colour-by metric.

const GRAIN_ORDER = ['whole', 'act', 'sequence', 'scene'];
// ⚠️ 'Script', not 'Whole' (owner, 2026-08-31: "in ui we see 'script' — be consistent").
// The rest of the product calls the top node Script (unitLabels.js:46 — hover, breadcrumbs,
// node panel), and the LP heading says "grades the whole script. Then every act, every
// sequence, every scene", so SCRIPT/ACT/SEQUENCE/SCENE is the one vocabulary that matches
// every other surface. The gutter used to be the only place the word 'Whole' appeared.
const GRAIN_LABEL = { whole: 'Script', act: 'Act', sequence: 'Sequence', scene: 'Scene' };
const NODE_TYPE   = { whole: 'movie', act: 'act', sequence: 'sequence', scene: 'scene' };
const ROW_STRIDE  = 40; // .lrs-row height (34) + .lrs-rows gap (6)

function Cell({ c, w, onSelect, setHover, interactive = true, mark = null }) {
  const ref = useRef(null);
  const [showLabel, setShowLabel] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const label = String(c.label == null ? '' : c.label);
    const need = label.length * 6.6 + 9;
    const check = () => setShowLabel(el.offsetWidth >= need);
    check();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(check); ro.observe(el); }
    return () => { if (ro) ro.disconnect(); };
  }, [c.label]);
  return (
    <button
      ref={ref}
      type="button"
      role="listitem"
      className={(interactive ? 'lrs-cell' : 'lrs-cell lrs-cell--static') + (mark === 'on' ? ' lrs-cell--marked' : mark === 'off' ? ' lrs-cell--unmarked' : '')}
      aria-label={c.label}
      // Static (phone minimap) cells are not targets: no click, no hover, out of the tab order.
      // The MapView below carries the interaction; leaving these clickable would keep the
      // 8px-sliver problem this mode exists to remove.
      tabIndex={interactive ? 0 : -1}
      aria-hidden={interactive ? undefined : true}
      onClick={() => interactive && onSelect && onSelect(c)}
      onMouseEnter={interactive ? (e) => { const r = e.currentTarget.getBoundingClientRect(); setHover({ node: c, x: r.left + r.width / 2, y: r.top, yb: r.bottom }); } : undefined}
      onMouseLeave={interactive ? () => setHover(null) : undefined}
      style={{
        flex: `0 0 ${w}%`,
        // ⚠️ softFill, not c.color. prepareChartData paints axis and lens views from the shared
        // `gradeColors` ramp, which carries two near-identical peach shades per band — adjacent
        // scores look the same and the weakest unit does not pop. The LR timeline has always
        // remapped that to a distinct 10-step scale; this strip did not, so the same metric on the
        // same data rendered visibly paler here than on the analysis page (owner, 2026-09-01).
        // Triage passes through untouched: softFill only substitutes values that are literally in
        // gradeColors, so the vivid band colours survive.
        background: softFill(c.color),
        borderRight: `1px solid ${c.border || 'rgba(0,0,0,.15)'}`,
        opacity: c.notOwed ? 0.5 : 1,
      }}
    >
      {showLabel ? <span className="lrs-cell-label">{c.label}</span> : null}
    </button>
  );
}

function HoverCard({ hover, movie, metric }) {
  let html = '';
  try { html = createCustomData(hover.node, movie, metric || 'overall', true); } catch (e) { html = ''; }
  if (!html) return null;
  const below = hover.y < 220;
  const style = {
    position: 'fixed', left: hover.x, top: below ? hover.yb + 8 : hover.y - 8,
    transform: below ? 'translate(-50%,0)' : 'translate(-50%,-100%)',
    background: '#fdfdfb', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 11px',
    borderRadius: 8, fontSize: 12.5, lineHeight: 1.45, maxWidth: 340, whiteSpace: 'normal',
    pointerEvents: 'none', zIndex: 2000, boxShadow: '0 6px 20px rgba(15,23,42,.22)',
  };
  const card = <div style={style} dangerouslySetInnerHTML={{ __html: html }} />;
  return typeof document !== 'undefined' ? createPortal(card, document.body) : card;
}

// marked: Set of node ids to pick out (a pattern's units); the rest of THAT row dims. Rows with no
// marked unit stay as they are, so the other levels still read.
export default function StripView({ nodes, grains, sizing = 'weight', onSelect, movie, metric, interactive = true, marked = null }) {
  const [hover, setHover] = useState(null);

  const rows = (grains && grains.length ? grains : ['scene'])
    .slice()
    .sort((a, b) => GRAIN_ORDER.indexOf(a) - GRAIN_ORDER.indexOf(b));

  const wOf = (c) => (sizing === 'weight' ? (c.value || 1) : 1);
  const cellsFor = (grain) => {
    const t = NODE_TYPE[grain] || grain;
    return nodes.filter((n) => n.nodeType === t).slice().sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));
  };
  const boundaries = (grain) => {
    const cells = cellsFor(grain);
    const tot = cells.reduce((s, c) => s + wOf(c), 0) || 1;
    const out = [];
    let acc = 0;
    for (let i = 0; i < cells.length - 1; i++) { acc += wOf(cells[i]); out.push((acc / tot) * 100); }
    return out;
  };
  const topFor = (grain) => { const i = rows.indexOf(grain); return i > 0 ? i * ROW_STRIDE : 0; };

  // ⚠️ THE TOP BLOCK NAMES THE FILM (owner, 2026-08-31). unitLabels.js gives the movie node the
  // label 'Script', which is right inside a paid analysis — you already know whose script it is —
  // and wrong on a landing page that switches between three films, where the top block is the one
  // place the script can say what it is. With the gutter now reading SCRIPT, printing 'Script'
  // inside it as well was the same word twice: label and value must not be the same string.
  // `movie` is the whole payload here (LRSnapshot passes movie={data}), so the title is one level
  // in; the fallback keeps the old label if a caller ever passes the movie node directly.
  const withTitle = (grain, c) => {
    if (grain !== 'whole') return c;
    const title = (movie && movie.movie && movie.movie.title) || (movie && movie.title);
    return title ? { ...c, label: title } : c;
  };

  const seqB = cellsFor('sequence').length ? boundaries('sequence') : [];
  const actB = cellsFor('act').length ? boundaries('act') : [];
  const seqTop = topFor('sequence');
  const actTop = topFor('act');

  return (
    <div className={interactive ? 'lrs-wrap2' : 'lrs-wrap2 lrs-wrap2--static'} onMouseLeave={() => setHover(null)}>
      <div className="lrs-gutter" aria-hidden="true">
        {rows.map((g) => <div key={g} className="lrs-rlab">{GRAIN_LABEL[g] || g}</div>)}
      </div>
      <div className="lrs-rows">
        {rows.map((grain) => {
          const cells = cellsFor(grain);
          if (!cells.length) return <div key={grain} className="lrs-row lrs-row-empty" />;
          const tot = cells.reduce((s, c) => s + wOf(c), 0) || 1;
          const rowMarked = !!(marked && marked.size && cells.some((c) => marked.has(c.id)));
          return (
            <div key={grain} className="lrs-row" role="list" aria-label={`${grain} strip`}>
              {cells.map((c) => (
                <Cell key={c.id} c={withTitle(grain, c)} w={(wOf(c) / tot) * 100} onSelect={onSelect} setHover={setHover} interactive={interactive}
                      mark={rowMarked ? (marked.has(c.id) ? 'on' : 'off') : null} />
              ))}
            </div>
          );
        })}
        <div className="lrs-bounds" aria-hidden="true">
          {seqB.map((x, i) => <span key={'s' + i} className="lrs-bound lrs-bound-seq" style={{ left: x + '%', top: seqTop + 'px' }} />)}
          {actB.map((x, i) => <span key={'a' + i} className="lrs-bound lrs-bound-act" style={{ left: x + '%', top: actTop + 'px' }} />)}
        </div>
      </div>
      {hover ? <HoverCard hover={hover} movie={movie} metric={metric} /> : null}
    </div>
  );
}
